import { cn } from './cn';

/**
 * The busy indicator, in one place.
 *
 * The same markup — `animate-spin rounded-full h-8 w-8 border-b-2
 * border-primary` — was copied into five components in the product, none of
 * which announced anything, and the admin console span a `Loader2` icon
 * instead, which is a different shape entirely.
 *
 * `role="status"` makes it a polite live region and the visually hidden label
 * is what gets read. `aria-hidden` on the ring keeps the decoration out of it.
 *
 * The label is a required prop rather than a translated default, because a
 * shared package cannot reach either app's i18n. The product wraps this and
 * supplies the translation.
 */
interface SpinnerProps extends React.ComponentProps<'div'> {
  /** What is loading, for anyone who cannot see the ring turn. */
  label: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizes = {
  sm: 'size-4 border-2',
  md: 'size-8 border-b-2',
  lg: 'size-12 border-b-2',
} as const;

export function Spinner({ label, size = 'md', className, ...props }: SpinnerProps) {
  return (
    <div role="status" className={cn('inline-flex items-center', className)} {...props}>
      <span
        aria-hidden="true"
        className={cn('animate-spin rounded-full border-primary', sizes[size])}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}
